import { z } from "zod";

/**
 * The coordinate conventions the 3D panel reads data in, each with the one
 * mapping that carries it into the scene.
 *
 * The scene has a single convention, Babylon's own axes: **Right = +X, Up = +Y,
 * Forward = +Z**. Anything drawn at identity faces +Z with its right on +X and
 * its top on +Y, and every convention below says where its source axes land in
 * that frame. Positions, point clouds and vector fields fill their right/up/
 * forward slots from these mappings, and a rotation stated in a convention is
 * converted through the same mapping — so data bound to one source frame lines
 * up wherever it is drawn. See `devlog/coordinate-fix.md`.
 *
 * The scene's axes are left-handed, so every right-handed source enters through
 * exactly one reflection: a right-handed convention's mapping has determinant
 * −1, and only `scene` has +1. `conventions.test.ts` checks that for every entry.
 *
 * Geographic conventions put north on +Z and east on +X: seen from above with
 * +Z up the screen, the map reads the right way round.
 */
export const conventionIdSchema = z.enum([
  "flu",
  "enu",
  "ned",
  "frd",
  "rdf",
  "scene",
] as const);

export type ConventionId = z.infer<typeof conventionIdSchema>;

export type SourceAxis = "x" | "y" | "z";

/** Which source axis feeds one scene axis, and with what sign. */
export interface ConventionAxis {
  field: SourceAxis;
  scale: 1 | -1;
}

export interface Convention {
  id: ConventionId;
  /** What each source axis points at, named the way the convention names it. */
  axisNames: Readonly<Record<SourceAxis, string>>;
  /** Whether the source frame is right-handed. Only `scene` is not. */
  handedness: "right" | "left";
  /** The source of scene +X. */
  right: ConventionAxis;
  /** The source of scene +Y. */
  up: ConventionAxis;
  /** The source of scene +Z. */
  forward: ConventionAxis;
}

export const CONVENTIONS: Readonly<Record<ConventionId, Convention>> = {
  // REP-103's body frame, and the frame a URDF is authored in. Also the panel's
  // default for ROS world frames such as odom and map, so a robot at identity
  // faces the scene's forward.
  flu: {
    id: "flu",
    axisNames: { x: "Forward", y: "Left", z: "Up" },
    handedness: "right",
    right: { field: "y", scale: -1 },
    up: { field: "z", scale: 1 },
    forward: { field: "x", scale: 1 },
  },
  // REP-103's frame for geographic positions.
  enu: {
    id: "enu",
    axisNames: { x: "East", y: "North", z: "Up" },
    handedness: "right",
    right: { field: "x", scale: 1 },
    up: { field: "z", scale: 1 },
    forward: { field: "y", scale: 1 },
  },
  // PX4's local frame, and ROS's `_ned` frames.
  ned: {
    id: "ned",
    axisNames: { x: "North", y: "East", z: "Down" },
    handedness: "right",
    right: { field: "y", scale: 1 },
    up: { field: "z", scale: -1 },
    forward: { field: "x", scale: 1 },
  },
  // PX4's body frame. Same mapping as `ned`: forward and north both land on
  // +Z, so a vehicle with a heading of 0 faces north.
  frd: {
    id: "frd",
    axisNames: { x: "Forward", y: "Right", z: "Down" },
    handedness: "right",
    right: { field: "y", scale: 1 },
    up: { field: "z", scale: -1 },
    forward: { field: "x", scale: 1 },
  },
  // ROS's `_optical` camera frames.
  rdf: {
    id: "rdf",
    axisNames: { x: "Right", y: "Down", z: "Forward" },
    handedness: "right",
    right: { field: "x", scale: 1 },
    up: { field: "y", scale: -1 },
    forward: { field: "z", scale: 1 },
  },
  // Data already in the scene's axes.
  scene: {
    id: "scene",
    axisNames: { x: "Right", y: "Up", z: "Forward" },
    handedness: "left",
    right: { field: "x", scale: 1 },
    up: { field: "y", scale: 1 },
    forward: { field: "z", scale: 1 },
  },
};

const SOURCE_INDEX: Record<SourceAxis, number> = { x: 0, y: 1, z: 2 };

/** Where a source vector lands in the scene, as `[X, Y, Z]`. */
export function toScene(
  convention: Convention,
  source: readonly [number, number, number],
): [number, number, number] {
  const read = (axis: ConventionAxis) =>
    // `+ 0` turns the `-0` a negative scale makes of a zero back into 0.
    axis.scale * source[SOURCE_INDEX[axis.field]] + 0;
  return [
    read(convention.right),
    read(convention.up),
    read(convention.forward),
  ];
}

/**
 * The determinant of a convention's mapping: −1 when it reflects (every
 * right-handed source), +1 when it does not.
 */
export function determinantOf(convention: Convention): number {
  const rows = [convention.right, convention.up, convention.forward].map(
    (axis) => {
      const row = [0, 0, 0];
      row[SOURCE_INDEX[axis.field]] = axis.scale;
      return row;
    },
  );
  return (
    rows[0][0] * (rows[1][1] * rows[2][2] - rows[1][2] * rows[2][1]) -
    rows[0][1] * (rows[1][0] * rows[2][2] - rows[1][2] * rows[2][0]) +
    rows[0][2] * (rows[1][0] * rows[2][1] - rows[1][1] * rows[2][0])
  );
}

/**
 * The convention a rotated body's own axes are given in, for a rotation stated
 * in `world`.
 *
 * The world's own convention, except for the two world frames whose bodies are
 * named differently: a robot moving in an East/North/Up frame keeps REP-103's
 * forward/left/up body, so its yaw of 0 faces east; and PX4's attitude turns a
 * forward/right/down body into North/East/Down, so its heading of 0 faces
 * north. `ned` and `frd` share a mapping, so the second changes no rotation —
 * only what its axes are called.
 */
export function bodyConventionFor(world: ConventionId): ConventionId {
  if (world === "enu") {
    return "flu";
  }
  return world === "ned" ? "frd" : world;
}
